import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Link } from "react-router-dom";
import {
  Wallet,
  Lightning,
  ChartLineUp,
  Circuitry,
  Plus,
  Spinner,
  Receipt,
  ArrowRight,
  CurrencyCircleDollar,
  Clock,
  FileText,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { getErrorMessage } from "../lib/utils";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TenantDashboard = () => {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [devices, setDevices] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [tariff, setTariff] = useState(null);
  const [consumptionData, setConsumptionData] = useState([]);
  const [consumptionAllowed, setConsumptionAllowed] = useState(true);
  const [meterStatus, setMeterStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, devicesRes, transactionsRes, tariffRes, chartRes, meterRes] = await Promise.all([
        axios.get(`${API_URL}/api/tenant/stats`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/devices`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/transactions`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/tariffs/active`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/consumption/chart?days=14`).catch(() => ({ data: { allowed: true, data: [] } })),
        axios.get(`${API_URL}/api/my-meter-status`).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setDevices(devicesRes.data || []);
      setTransactions(transactionsRes.data || []);
      setTariff(tariffRes.data);
      setMeterStatus(meterRes.data);
      const chartData = chartRes.data || { allowed: true, data: [] };
      setConsumptionAllowed(chartData.allowed !== false);
      setConsumptionData(chartData.data || []);
      refreshUser();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getBalanceStatus = () => {
    const kwh = meterStatus?.success ? (meterStatus.balance_energy_kwh || 0) : 0;
    if (!meterStatus?.success) return { status: "warning", message: "Sayac verisi alinamadi" };
    if (kwh <= 0) return { status: "critical", message: "Sayac bakiyesi bitti!" };
    if (kwh < 10) return { status: "warning", message: "Bakiye azaliyor" };
    return { status: "good", message: meterStatus.online ? "Cevrimici" : "Cevrimdisi" };
  };

  const balanceStatus = getBalanceStatus();

  const downloadReceipt = async (txnId) => {
    try {
      const res = await axios.get(`${API_URL}/api/transactions/${txnId}/receipt-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `makbuz_${txnId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Makbuz indirilemedi"); }
  };

  // Build chart from last 5 transactions
  const chartData = transactions
    .slice(0, 7)
    .reverse()
    .map((tx, i) => ({
      name: new Date(tx.created_at).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" }),
      bakiye: tx.balance_after,
    }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size={32} className="animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="tenant-dashboard">
      {/* Meter Balance Card */}
      <Card className={`border-2 ${
        balanceStatus.status === "critical" ? "border-red-200 bg-red-50" :
        balanceStatus.status === "warning" ? "border-amber-200 bg-amber-50" :
        "border-emerald-200 bg-emerald-50"
      } shadow-sm`}>
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center shrink-0 ${
                balanceStatus.status === "critical" ? "bg-red-100" :
                balanceStatus.status === "warning" ? "bg-amber-100" :
                "bg-emerald-100"
              }`}>
                <Circuitry size={28} weight="duotone" className={
                  balanceStatus.status === "critical" ? "text-red-600" :
                  balanceStatus.status === "warning" ? "text-amber-600" :
                  "text-emerald-600"
                } />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-600">Sayac Bakiyesi</p>
                <p className="text-2xl sm:text-4xl font-bold text-slate-900" data-testid="tenant-balance">
                  {meterStatus?.success ? (meterStatus.balance_energy_kwh || 0).toFixed(2) : "--"}
                  <span className="text-base sm:text-xl text-slate-400 font-normal ml-2">kWh</span>
                </p>
                {meterStatus?.success && meterStatus.device_name && (
                  <p className="text-xs text-slate-500 mt-1" data-testid="device-info">
                    {meterStatus.device_name}
                  </p>
                )}
                <Badge
                  className={`mt-1 ${
                    balanceStatus.status === "critical" ? "bg-red-100 text-red-700" :
                    balanceStatus.status === "warning" ? "bg-amber-100 text-amber-700" :
                    "bg-emerald-100 text-emerald-700"
                  }`}
                  data-testid="balance-status-badge"
                >
                  {balanceStatus.message}
                </Badge>
              </div>
            </div>
            <Link to="/topup">
              <Button
                className="bg-indigo-600 hover:bg-indigo-700 h-12 px-6"
                data-testid="topup-btn"
              >
                <Plus size={20} className="mr-2" />
                Bakiye Yukle
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-3 sm:gap-6">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-3 sm:p-5">
            <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium text-slate-500">Anlik Guc</p>
                <p className="text-lg sm:text-3xl font-bold text-slate-900 mt-0.5 sm:mt-1" data-testid="stat-power">
                  {meterStatus?.success ? (meterStatus.power || 0) : (stats?.current_power_w || 0)}
                  <span className="text-xs sm:text-lg text-slate-400 font-normal ml-0.5 sm:ml-1">W</span>
                </p>
              </div>
              <div className="hidden sm:flex w-12 h-12 bg-indigo-50 rounded-2xl items-center justify-center">
                <Lightning size={24} weight="duotone" className="text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-3 sm:p-5">
            <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium text-slate-500">Toplam Tuketim</p>
                <p className="text-lg sm:text-3xl font-bold text-slate-900 mt-0.5 sm:mt-1" data-testid="stat-energy">
                  {meterStatus?.success ? (meterStatus.total_energy_kwh || 0).toFixed(2) : (stats?.total_energy_kwh || 0)}
                  <span className="text-xs sm:text-lg text-slate-400 font-normal ml-0.5 sm:ml-1">kWh</span>
                </p>
              </div>
              <div className="hidden sm:flex w-12 h-12 bg-emerald-50 rounded-2xl items-center justify-center">
                <ChartLineUp size={24} weight="duotone" className="text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-3 sm:p-5">
            <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium text-slate-500">Aylik Yukleme</p>
                <p className="text-lg sm:text-3xl font-bold text-slate-900 mt-0.5 sm:mt-1" data-testid="stat-topup">
                  {"\u20ba"}{stats?.monthly_topup?.toFixed(0) || "0"}
                </p>
              </div>
              <div className="hidden sm:flex w-12 h-12 bg-purple-50 rounded-2xl items-center justify-center">
                <CurrencyCircleDollar size={24} weight="duotone" className="text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart & Tariff */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Balance History Chart */}
        <Card className="border-slate-200 shadow-sm lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <ChartLineUp size={20} className="text-indigo-600" />
              Bakiye Gecmisi
            </CardTitle>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" stroke="#94A3B8" fontSize={12} />
                    <YAxis stroke="#94A3B8" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#fff",
                        border: "1px solid #E2E8F0",
                        borderRadius: "12px",
                        boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                      }}
                      formatter={(value) => [`${"\u20ba"}${value.toFixed(2)}`, "Bakiye"]}
                    />
                    <Area
                      type="monotone"
                      dataKey="bakiye"
                      stroke="#4F46E5"
                      fill="#EEF2FF"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center">
                <div className="text-center">
                  <ChartLineUp size={40} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-sm text-slate-500">Henuz islem gecmisi yok</p>
                  <Link to="/topup">
                    <Button size="sm" className="mt-3 bg-indigo-600 hover:bg-indigo-700">
                      Ilk Yuklemeyi Yap
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Current Tariff */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-heading">Gecerli Tarife</CardTitle>
          </CardHeader>
          <CardContent>
            {tariff ? (
              <div className="space-y-3">
                <p className="text-sm font-medium text-indigo-600 mb-3">{tariff.name}</p>
                {tariff.tiers?.map((tier, index) => {
                  const monthlyKwh = stats?.monthly_kwh_loaded ?? user?.monthly_kwh_loaded ?? 0;
                  const isCurrentTier = monthlyKwh >= (tier.min_kwh || 0) && (tier.max_kwh == null || monthlyKwh < tier.max_kwh);
                  return (
                    <div key={index} className={`p-3 rounded-xl border ${isCurrentTier ? "bg-indigo-50 border-indigo-200" : "bg-slate-50 border-slate-100"}`}>
                      <div className="flex justify-between items-center mb-1">
                        <span className={`text-sm ${isCurrentTier ? "text-indigo-700 font-medium" : "text-slate-600"}`}>
                          {tariff.tiers.length === 1 ? "Sabit Fiyat" : `${tier.min_kwh} - ${tier.max_kwh || "\u221e"} kWh`}
                          {isCurrentTier && " (Mevcut)"}
                        </span>
                        <span className={`text-sm font-semibold ${isCurrentTier ? "text-indigo-700" : "text-indigo-600"}`}>
                          {"\u20ba"}{(tier.price_per_kwh || 0).toFixed(4)}
                        </span>
                      </div>
                      {tariff.tiers.length > 1 && (
                        <Progress value={(index + 1) * (100 / tariff.tiers.length)} className="h-1" />
                      )}
                    </div>
                  );
                })}
                {(stats?.monthly_kwh_loaded || user?.monthly_kwh_loaded) > 0 && (
                  <p className="text-xs text-slate-500 mt-2" data-testid="monthly-kwh-info">
                    Bu ay yuklenen: {(stats?.monthly_kwh_loaded ?? user?.monthly_kwh_loaded ?? 0).toFixed(1)} kWh
                  </p>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <Receipt size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-slate-500">Tarife bilgisi yok</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Consumption Chart - only if customer allows */}
      {consumptionAllowed && (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <ChartLineUp size={20} className="text-emerald-600" />
              Son 14 Gun - Yukleme ve Tuketim
            </CardTitle>
          </CardHeader>
          <CardContent>
            {consumptionData.length > 0 ? (
              <div className="h-56" data-testid="tenant-consumption-chart">
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                  <AreaChart data={consumptionData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="label" stroke="#94A3B8" fontSize={11} />
                    <YAxis stroke="#94A3B8" fontSize={11} />
                    <Tooltip
                      contentStyle={{ backgroundColor: "#fff", border: "1px solid #E2E8F0", borderRadius: "12px" }}
                      formatter={(value, name) => [`₺${Number(value).toFixed(2)}`, name === "topup" ? "Yukleme" : t("reports.consumption")]}
                    />
                    <Area type="monotone" dataKey="topup" name="Yukleme" stroke="#4F46E5" fill="#EEF2FF" strokeWidth={2} />
                    <Area type="monotone" dataKey="cost" name={t("reports.consumption")} stroke="#F59E0B" fill="#FEF3C7" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-56 flex items-center justify-center">
                <div className="text-center">
                  <ChartLineUp size={40} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-sm text-slate-500">Henuz tuketim verisi yok</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recent Transactions */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-heading">{t("dashboard.recent_transactions")}</CardTitle>
          <Link to="/reports" className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1">
            Tumunu Gor <ArrowRight size={16} />
          </Link>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <div className="text-center py-8">
              <Receipt size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-sm text-slate-500">Henuz islem yok</p>
              <p className="text-xs text-slate-400 mt-1">Bakiye yukleyerek baslayin</p>
            </div>
          ) : (
            <div className="space-y-3">
              {transactions.slice(0, 5).map((tx) => (
                <div
                  key={tx.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
                  data-testid={`transaction-${tx.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      tx.type === "topup" ? "bg-emerald-100" : 
                      tx.type === "consumption" ? "bg-red-100" : "bg-amber-100"
                    }`}>
                      {tx.type === "topup" ? (
                        <Plus size={20} className="text-emerald-600" />
                      ) : tx.type === "consumption" ? (
                        <Lightning size={20} className="text-red-600" />
                      ) : (
                        <Receipt size={20} className="text-amber-600" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{tx.description}</p>
                      <p className="text-xs text-slate-500">
                        {new Date(tx.created_at).toLocaleString("tr-TR")}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`font-semibold ${
                      tx.amount > 0 ? "text-emerald-600" : "text-red-600"
                    }`}>
                      {tx.amount > 0 ? "+" : ""}{"\u20ba"}{(tx.amount || 0).toFixed(2)}
                    </span>
                    <p className="text-xs text-slate-400">{"\u20ba"}{(tx.balance_after || 0).toFixed(2)}</p>
                  </div>
                  <button onClick={() => downloadReceipt(tx.id)}
                    className="text-indigo-600 hover:text-indigo-700 ml-3 shrink-0" title="Makbuzu Indir"
                    data-testid={`download-receipt-${tx.id}`}>
                    <FileText size={18} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Device Info */}
      {devices.length > 0 && (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-heading">Sayac Bilgileri</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {devices.map((device) => (
                <div
                  key={device.id}
                  className="p-4 border border-slate-200 rounded-xl"
                  data-testid={`device-info-${device.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center">
                        <Circuitry size={24} weight="duotone" className="text-indigo-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-slate-900">{device.name}</h4>
                        <p className="text-sm text-slate-500">
                          {device.apartment_name && device.unit_name
                            ? `${device.apartment_name} - ${device.unit_name}`
                            : "Konum bilgisi yok"}
                        </p>
                      </div>
                    </div>
                    <Badge className={device.online ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}>
                      {device.online ? "Cevrimici" : "Cevrimdisi"}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-3 border-t border-slate-100">
                    <div>
                      <p className="text-xs text-slate-400">Anlik Guc</p>
                      <p className="text-lg font-semibold text-slate-900">
                        {device.current_power_w || 0} W
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Toplam Tuketim</p>
                      <p className="text-lg font-semibold text-slate-900">
                        {device.total_energy_kwh || 0} kWh
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No devices message */}
      {devices.length === 0 && (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="py-12 text-center">
            <Circuitry size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">Henuz sayac atanmamis</h3>
            <p className="text-sm text-slate-500">Apartman sahibiniz daireye sayac atadiktan sonra burada gorunecektir</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TenantDashboard;
